/**
 * EXECUTION BARRIER MAP — presentation only.
 *
 * Answers one question for a work package: WHAT PREVENTS THIS WORK FROM
 * EXECUTING? Every answer is a backend fact. This module groups, labels and
 * orders-by-nothing; it evaluates no gate and decides no state.
 *
 * THE ONE DESIGN DECISION WORTH READING
 *
 * `executable-work-package-1` gives a status PER INSTANCE. It does NOT give a
 * status per package, deliberately — a package holding one BLOCKED instance,
 * three IN_PROGRESS and two UNKNOWN has no single honest answer.
 *
 * So this module does NOT compute one. It renders the instance states the
 * backend sent and tallies how many are in each, which is presentation of
 * given facts. Fusing them into a package verdict would be a second state
 * machine living in React, and it would have to invent a precedence rule
 * ("does one BLOCKED outrank four COMPLETE?") that no contract owns.
 *
 * A tally is not a score. The counts are never summed, weighted, compared
 * across packages, or turned into an ordering.
 */
import {
  RUNTIME_STATES,
  runtimeStateDisplay,
  presentContractVersion,
} from './runtimeVocabulary';

/** The merged contracts this surface is written against. */
export const BARRIER_CONTRACTS = {
  workPackages: 'executable-work-package-1',
  leverage: 'constraint-leverage-1',
};

/**
 * Gate status as the gating engine emits it.
 *
 * NOT_REQUIRED is not a condition on this work at all — it is the engine
 * saying the gate does not apply. Showing it among barriers would pad the
 * list with non-problems.
 */
export const GATE_STATUSES = ['PASSED', 'FAILED', 'NOT_REQUIRED', 'UNKNOWN'];

/**
 * One gate's truth state, mapped from the two fields the backend sets.
 *
 * `conflict` wins over `status`: the requirement adapter marks a disputed
 * obligation structurally, and a dispute is not a failure — it is two sources
 * disagreeing, and its resolution is a human reading both.
 */
export const truthStateForGate = (gate) => {
  const { status, conflict } = gate || {};
  if (conflict) return 'CONFLICT';
  if (status === 'PASSED') return 'PASS';
  if (status === 'FAILED') return 'FAIL';
  // UNKNOWN, NOT_REQUIRED, and anything unrecognised all decline to assert.
  return 'UNKNOWN';
};

/**
 * One condition, as a row.
 *
 * `detail` is the engine's own prose and is rendered verbatim — never
 * reworded, because the wording carries which record to go and look at.
 */
export const presentCondition = (gate) => {
  const g = gate || {};
  return {
    gate: g.gate || null,
    number: typeof g.number === 'number' ? g.number : null,
    /**
     * The engine's raw status is kept alongside the mapped truth state,
     * because NOT_REQUIRED and UNKNOWN both decline to assert but mean
     * opposite things: one is "this gate does not apply", the other is
     * "this gate applies and nobody has answered it".
     */
    status: GATE_STATUSES.includes(g.status) ? g.status : 'UNKNOWN',
    applicable: g.status !== 'NOT_REQUIRED',
    state: truthStateForGate(g),
    hard: Boolean(g.hard),
    /** The engine's sentence, untouched. */
    detail: g.detail || null,
    sourceTable: g.sourceTable || null,
    sourceIds: Array.isArray(g.sourceIds) ? g.sourceIds.filter(Boolean) : [],
    conflict: Boolean(g.conflict),
  };
};

/**
 * Split one instance's gates into the four groups a superintendent acts on
 * differently.
 *
 * The grouping key is the backend's own `status`/`hard`/`conflict` — nothing
 * here decides whether a gate blocks. A conflicted gate goes to `conflicts`
 * and to nothing else, so it is never counted as a failure.
 */
export const partitionConditions = (gates) => {
  const all = (Array.isArray(gates) ? gates : []).map(presentCondition);
  // A NOT_REQUIRED gate is the engine saying this gate does not apply to this
  // work. It is not a barrier, and it is not an open question either.
  const rows = all.filter((r) => r.applicable);
  return {
    /** A hard gate the backend reports FAILED. */
    blocking: rows.filter((r) => r.state === 'FAIL' && r.hard),
    /** A failed gate that is not hard — a condition, not a stop. */
    conditional: rows.filter((r) => r.state === 'FAIL' && !r.hard),
    /** Applies, and nobody has answered it. Never a pass, never a block. */
    unknown: rows.filter((r) => r.state === 'UNKNOWN' && r.status === 'UNKNOWN'),
    /** Sources disagree. Its own group, with its own resolution. */
    conflicts: rows.filter((r) => r.state === 'CONFLICT'),
    /** Kept so a reader can see what WAS checked, not only what failed. */
    satisfied: rows.filter((r) => r.state === 'PASS'),
    /** Gates the engine says do not apply here. Never rendered as barriers. */
    notApplicable: all.filter((r) => !r.applicable),
  };
};

/**
 * One instance, with its backend status and its conditions.
 *
 * `canStart` is rendered from the backend's own field. It is never derived
 * from the status string here — the backend owns that equivalence.
 */
export const presentInstance = (instance) => {
  const i = instance || {};
  const conditions = partitionConditions(i.gates);
  return {
    instanceId: i.instanceId || null,
    instanceName: i.instanceName || null,
    activityId: i.activityId || null,
    status: RUNTIME_STATES.includes(i.status) ? i.status : 'UNKNOWN',
    /** Backend-owned. Absent stays absent rather than becoming false. */
    canStart: typeof i.canStart === 'boolean' ? i.canStart : null,
    governingConstraint: i.governingConstraint || null,
    conditions,
    missingFacts: Array.isArray(i.missingFacts) ? i.missingFacts.filter(Boolean) : [],
    /**
     * ERS is only meaningful when the backend says it is known. A null date
     * rendered as a blank cell reads as "no constraint on the start".
     */
    earliestRealisticStart: i.ersKnown ? i.earliestRealisticStart || null : null,
    ersKnown: Boolean(i.ersKnown),
  };
};

/**
 * How many instances are in each runtime state.
 *
 * A tally of backend-given values, in the vocabulary's own fixed order — NOT
 * sorted by size, because sorting by size is a ranking and reads as priority.
 * States with no instances are present with zero so the shape is stable and a
 * reader can see that a state was considered and found empty.
 */
export const tallyByState = (instances) => {
  const rows = Array.isArray(instances) ? instances : [];
  return RUNTIME_STATES.map((state) => ({
    state,
    label: runtimeStateDisplay(state).label,
    count: rows.filter((i) => i.status === state).length,
  }));
};

/**
 * One leverage constraint.
 *
 * Every figure is the backend's. The notes that ship with `unlocks`,
 * `soleHardFailedGateOf` and `downstreamExposure` are carried verbatim,
 * because each is the contract stating a limit on what its own number means —
 * exactly the part a UI is tempted to drop.
 */
export const presentConstraint = (constraint) => {
  const c = constraint || {};
  const block = (b) => ({
    count: typeof b?.count === 'number' ? b.count : null,
    instanceIds: Array.isArray(b?.instanceIds) ? b.instanceIds : [],
    note: b?.note || null,
  });
  const [sourceTable, sourceId] = String(c.constraintKey || '').split(':');
  return {
    constraintKey: c.constraintKey || null,
    tier: c.tier || null,
    kind: c.kind || null,
    title: c.title || null,
    hard: Boolean(c.hard),
    gateKeys: Array.isArray(c.gateKeys) ? c.gateKeys : [],
    /**
     * The row that determines this constraint, recovered from the key the
     * contract defines as `{source_table}:{source_id}`. INSTANCE_SCOPED keys
     * are `instance:{id}:{gate}` and carry no table — the vocabulary maps no
     * label for `instance`, so it renders without a guessed noun.
     */
    sourceTable: c.tier === 'EXACT' ? sourceTable || null : null,
    sourceIds: c.tier === 'EXACT' && sourceId ? [sourceId] : [],
    holdsNow: block(c.holdsNow),
    soleHardFailedGateOf: block(c.soleHardFailedGateOf),
    unlocks: block(c.unlocks),
    downstreamExposure: {
      ...block(c.downstreamExposure),
      /** The contract's own flag. Rendered as exposure, never as a promise. */
      counterfactual: Boolean(c.downstreamExposure?.counterfactual),
    },
    unknownHolds: block(c.unknownHolds),
  };
};

/**
 * A constraint carrying only unknowns, or a disputed one.
 *
 * Both arrive from the contract in a REDUCED shape — identity and `touches`,
 * with the leverage figures STRUCTURALLY ABSENT rather than zeroed. Reading a
 * missing count as 0 would turn "we cannot say" into "it unlocks nothing".
 */
export const presentReducedConstraint = (constraint) => {
  const c = constraint || {};
  return {
    constraintKey: c.constraintKey || null,
    tier: c.tier || null,
    kind: c.kind || null,
    title: c.title || null,
    hard: Boolean(c.hard),
    touches: {
      count: typeof c.touches?.count === 'number' ? c.touches.count : null,
      instanceIds: Array.isArray(c.touches?.instanceIds) ? c.touches.instanceIds : [],
    },
    /** Asserted so a caller cannot accidentally read a leverage figure. */
    hasLeverageFigures: false,
  };
};

/**
 * One work package's barrier map.
 *
 * Constraints are rendered in the order the contract returns them. The
 * contract states its own ordering and calls it attention, not priority; this
 * module neither re-sorts nor labels the order.
 */
export const presentWorkPackage = (pkg) => {
  const p = pkg || {};
  const wp = p.workPackage || {};
  const instances = (Array.isArray(p.instances) ? p.instances : []).map(presentInstance);
  const blocking = p.blockingSet || {};
  return {
    kind: wp.kind || null,
    bidPackageId: wp.bidPackageId || null,
    title: wp.title || null,
    trade: wp.trade || null,
    instances,
    /** Presentation of given states. Never fused into one package verdict. */
    tally: tallyByState(instances),
    instanceCount: instances.length,
    constraints: (Array.isArray(blocking.constraints) ? blocking.constraints : []).map(
      presentConstraint
    ),
    conflicts: (Array.isArray(blocking.conflicts) ? blocking.conflicts : []).map(
      presentReducedConstraint
    ),
    unknownOnly: (Array.isArray(blocking.unknownOnly) ? blocking.unknownOnly : []).map(
      presentReducedConstraint
    ),
    unknowns: Array.isArray(blocking.unknowns) ? blocking.unknowns : [],
    /**
     * Package-level conflicts are a different family from the blocking set's:
     * lineage divergence, multi-package claims, refused field observations.
     */
    packageConflicts: Array.isArray(p.conflicts) ? p.conflicts : [],
    missingInformation: Array.isArray(p.missingInformation)
      ? p.missingInformation.filter(Boolean)
      : [],
    sourceFingerprint: p.sourceFingerprint || null,
    /**
     * A package with no instances is NOT a package with nothing blocking it.
     * The contract says so and the UI must repeat it.
     */
    hasInstances: instances.length > 0,
  };
};

/**
 * The whole response.
 *
 * `contract` is checked but never enforced here: a mismatch is surfaced to
 * the reader rather than turned into an empty screen, because an additive
 * version still carries every field this view reads.
 */
export const presentBarrierMap = (payload, { supported = BARRIER_CONTRACTS.workPackages } = {}) => {
  const p = payload || {};
  const packages = (Array.isArray(p.workPackages) ? p.workPackages : []).map(presentWorkPackage);
  return {
    contract: presentContractVersion(p.contractVersion, supported),
    projectId: p.projectId || null,
    computedAt: p.computedAt || null,
    packages,
    conflicts: Array.isArray(p.conflicts) ? p.conflicts : [],
    missingInformation: Array.isArray(p.missingInformation)
      ? p.missingInformation.filter(Boolean)
      : [],
    /**
     * No packages is its own statement, and it is not "nothing is blocked".
     * The caller renders `missingInformation`, which names why.
     */
    hasPackages: packages.length > 0,
  };
};

/**
 * Find one package in the response by its bid package id.
 *
 * A filter over a given list. Returns null rather than the first package when
 * there is no match — showing a different package's barriers under this
 * package's heading is the worst failure this surface has.
 */
export const findPackage = (presented, bidPackageId) => {
  if (!presented || !bidPackageId) return null;
  return (presented.packages || []).find((p) => p.bidPackageId === bidPackageId) || null;
};

/**
 * Why the surface is unavailable, kept as four distinct answers.
 *
 * The execution-intelligence stack ANDs a global flag, a tenant entitlement
 * and — for the work-package view — the `rfq.read` capability. Collapsing
 * them into "unavailable" tells an operator nothing about who can fix it.
 */
export const presentBarrierAvailability = (error) => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const code = typeof data === 'object' && data ? data.code || data.detail?.code : null;

  if (status === 404) {
    return {
      kind: 'NOT_FOUND',
      title: 'This project was not found',
      detail:
        'It may belong to another tenant, or it may not exist. OMNΛS answers the same way for both, deliberately.',
    };
  }
  if (status === 503 || code === 'not_enabled') {
    return {
      kind: 'NOT_ENABLED',
      title: 'The execution runtime is switched off',
      detail: 'This is a platform-wide setting. Nothing about this project caused it.',
    };
  }
  if (status === 403 && code === 'tenant_not_entitled') {
    return {
      kind: 'NOT_ENTITLED',
      title: 'This tenant is not entitled to the execution runtime',
      detail: 'An OMNΛS operator grants this. It is not something a project admin can change.',
    };
  }
  if (status === 403 && code === 'capability_required') {
    return {
      kind: 'CAPABILITY_REQUIRED',
      title: 'You do not have the capability this view needs',
      detail:
        'The barrier map joins commercial identity to execution structure, so it needs the same access as bid pricing.',
    };
  }
  if (status === 403) {
    return {
      kind: 'ROLE_REFUSED',
      title: 'Your role cannot open this view',
      detail: 'Someone with commercial access on this project can.',
    };
  }
  return {
    kind: 'UNAVAILABLE',
    title: 'The barrier map could not be loaded',
    detail: 'This is a problem reaching OMNΛS, not a statement about the work.',
  };
};
